import { useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useClientPermissions } from "../apis/profileApis";
import { useSelector } from "react-redux";
import { selectClientPermissions } from "../features/auth/authSlice";

export interface TabConfig {
    key: string;
    label: string;
    component: React.ReactNode;
    modules: string[];
    operation: string;
}

interface UsePermissionTabsProps {
    tabs: TabConfig[];
    param: "tab" | "sub"; // which URL param to use
    parentTab?: string;   // for sub-tabs: ?tab=Parent&sub=Child
    fallbackKey?: string; // optional override
}

export const usePermissionTabs = ({
    tabs,
    param,
    parentTab,
    fallbackKey,
}: UsePermissionTabsProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { isLoading, error } = useClientPermissions();
    const permissions = useSelector(selectClientPermissions) ?? null;

    const activeKey = searchParams.get(param) || fallbackKey || tabs[0]?.key;

    const visibleTabs = useMemo(() => {
        if (!permissions) return [];
        return tabs.filter((tab) =>
            tab.modules.some((mod) =>
                permissions.some((p) => p.module === mod && p.operations.includes(tab.operation))
            )
        );
    }, [permissions, tabs]);

    // Auto-redirect if current tab is not allowed
    useEffect(() => {
        const isAllowed = visibleTabs.some((t) => t.key === activeKey);
        if (!isAllowed && visibleTabs.length > 0) {
            const newParams: Record<string, string> = {};
            if (parentTab) newParams[param === "sub" ? "tab" : "sub"] = parentTab;
            newParams[param] = visibleTabs[0].key;
            setSearchParams(newParams, { replace: true });
        }
    }, [activeKey, visibleTabs, setSearchParams, param, parentTab]);

    const handleChange = (label: string) => {
        const tab = visibleTabs.find((t) => t.label === label);
        if (tab) {
            const newParams: Record<string, string> = { [param]: tab.key };
            if (parentTab && param === "sub") newParams.tab = parentTab;
            setSearchParams(newParams);
        }
    };

    const activeLabel = visibleTabs.find((t) => t.key === activeKey)?.label ?? visibleTabs[0]?.label;
    const content = visibleTabs.find((t) => t.key === activeKey)?.component;

    return {
        isLoading,
        error,
        visibleTabs,
        activeKey,
        activeLabel,
        content,
        handleChange,
    };
};

type Permission = 'create' | 'read' | 'update' | 'delete';

type ModulePermission = {
    module: string;
    operations: Permission[];
};

export const hasPermission = (
    perms: ModulePermission[] = [],
    module: string,
    action: Permission
): boolean => {
    const normalizedModule = module.trim().toLowerCase();
    const normalizedAction = action.trim().toLowerCase();
    return perms?.some((p) => {
        if (!p?.module || !Array.isArray(p.operations)) return false;

        const moduleMatch =
            p.module.trim().toLowerCase() === normalizedModule;

        const permissionMatch = p?.operations?.some(
            (perm) => perm.trim().toLowerCase() === normalizedAction
        );

        return moduleMatch && permissionMatch;
    });
};

export const usePermission = (
    module: string,
    action: Permission
) => {
    const { isLoading, error } = useClientPermissions();
    const permissions = useSelector(selectClientPermissions);

    const allowed = hasPermission(permissions as any, module, action);

    return {
        allowed,
        isLoading,
        error,
    };
};
